/**
 * API-Metriken-Dashboard-Komponente für Admin-Dashboard
 * 
 * Zeigt Request-Rate, Error-Rate, Latency und Top Endpoints als Charts
 * 
 * @module components/admin/ApiMetrics
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, AlertCircle, RefreshCw, TrendingUp, AlertTriangle, Clock, Activity, BarChart3, Zap, Bug, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface ApiMetrics {
  requestRate: {
    '1m': number;
    '5m': number;
    '1h': number;
  };
  errorRate: {
    '1m': { rate4xx: number; rate5xx: number; total: number };
    '5m': { rate4xx: number; rate5xx: number; total: number };
    '1h': { rate4xx: number; rate5xx: number; total: number };
  };
  latency: {
    '1m': { p50: number; p95: number; p99: number };
    '5m': { p50: number; p95: number; p99: number };
    '1h': { p50: number; p95: number; p99: number };
  };
  topEndpoints: {
    slowest: Array<{
      path: string;
      method: string;
      avgDuration: number;
      p95Duration: number;
      p99Duration: number;
      count: number;
    }>;
    mostTraffic: Array<{
      path: string;
      method: string;
      count: number;
      avgDuration: number;
    }>;
    mostErrors: Array<{
      path: string;
      method: string;
      errorRate: number;
      errorCount: number;
      count: number;
    }>;
  };
  timestamp: string;
}

interface TimeSeriesData {
  timeSeries: Array<{
    timestamp: number;
    requestRate: number;
    errorRate4xx: number;
    errorRate5xx: number;
    p95Latency: number;
    p99Latency: number;
  }>;
  window: string;
  startTime: number;
  endTime: number;
  timestamp: string;
}

export function ApiMetrics() {
  const [timeWindow, setTimeWindow] = useState<'1h' | '6h' | '24h'>('1h');

  const { data: metrics, isLoading: isLoadingMetrics, error: metricsError, refetch: refetchMetrics } = useQuery<ApiMetrics>({
    queryKey: ["/api/admin/metrics"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/metrics");
      if (!res.ok) throw new Error("Fehler beim Laden der API-Metriken");
      return await res.json();
    },
    refetchInterval: 30000, // Alle 30 Sekunden aktualisieren
    refetchIntervalInBackground: false,
    gcTime: 60000,
  });

  const { data: timeSeriesData, isLoading: isLoadingTimeSeries } = useQuery<TimeSeriesData>({
    queryKey: ["/api/admin/metrics/time-series", timeWindow],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/metrics/time-series?window=${timeWindow}`);
      if (!res.ok) throw new Error("Fehler beim Laden der Time-Series-Metriken");
      return await res.json();
    },
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    gcTime: 60000,
  });

  // Recent errors query
  const { data: errorsData } = useQuery<{ errors: Array<{ timestamp: number; method: string; path: string; statusCode: number; duration: number; requestId?: string; userId?: string | number }>; count: number; uptime: { startedAt: number; uptimeMs: number }; timestamp: string }>({
    queryKey: ["/api/admin/metrics/errors"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/metrics/errors?limit=30");
      if (!res.ok) throw new Error("Fehler");
      return await res.json();
    },
    refetchInterval: 30000,
    gcTime: 60000,
  });

  // Slow requests query
  const { data: slowData } = useQuery<{ slowRequests: Array<{ timestamp: number; method: string; path: string; statusCode: number; duration: number; requestId?: string }>; count: number; threshold: number; timestamp: string }>({
    queryKey: ["/api/admin/metrics/slow"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/metrics/slow?limit=15");
      if (!res.ok) throw new Error("Fehler");
      return await res.json();
    },
    refetchInterval: 30000,
    gcTime: 60000,
  });

  const [showAllErrors, setShowAllErrors] = useState(false);
  const [showAllSlow, setShowAllSlow] = useState(false);

  // P0 FIX: Alle Hooks MÜSSEN vor early returns stehen (Rules of Hooks)
  // Stabilisiere die Dependency durch Serialisierung der relevanten Daten
  const timeSeriesArray = timeSeriesData?.timeSeries;
  
  // Erstelle einen stabilen Key basierend auf den Daten
  const timeSeriesKey = React.useMemo(() => {
    if (!timeSeriesArray || !Array.isArray(timeSeriesArray) || timeSeriesArray.length === 0) {
      return '';
    }
    // Erstelle einen stabilen Key basierend auf den Timestamps und Werten
    try {
      return JSON.stringify(
        timeSeriesArray.map(p => ({
          t: p?.timestamp,
          r: p?.requestRate,
          e4: p?.errorRate4xx,
          e5: p?.errorRate5xx,
          p95: p?.p95Latency,
          p99: p?.p99Latency
        }))
      );
    } catch {
      return '';
    }
  }, [timeSeriesArray]);
  
  const chartData = React.useMemo(() => {
    if (!timeSeriesArray || !Array.isArray(timeSeriesArray)) {
      return [];
    }
    
    return timeSeriesArray
      .filter(point => point && typeof point.timestamp === 'number')
      .map(point => ({
        time: new Date(point.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        requestRate: typeof point.requestRate === 'number' ? point.requestRate : 0,
        errorRate4xx: typeof point.errorRate4xx === 'number' ? point.errorRate4xx : 0,
        errorRate5xx: typeof point.errorRate5xx === 'number' ? point.errorRate5xx : 0,
        p95Latency: typeof point.p95Latency === 'number' ? point.p95Latency : 0,
        p99Latency: typeof point.p99Latency === 'number' ? point.p99Latency : 0,
      }));
  }, [timeSeriesKey]);
  
  // P0 Fix: Prüfe ob genug Datenpunkte für Chart vorhanden
  const hasEnoughData = chartData.length >= 2;

  // Early returns NACH allen Hooks
  if (isLoadingMetrics) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="mt-2 text-gray-500">Lade API-Metriken...</p>
      </div>
    );
  }

  if (metricsError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 mx-auto text-red-600" />
        <p className="mt-2 text-red-600">Fehler beim Laden der API-Metriken</p>
        <Button
          onClick={() => refetchMetrics()}
          variant="outline"
          size="sm"
          className="mt-4"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Erneut versuchen
        </Button>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 mx-auto text-gray-400" />
        <p className="mt-2 text-gray-500">Keine Metriken verfügbar</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Übersicht-Karten - Mobile optimiert */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">Request-Rate (1h)</p>
              <p className="text-xl md:text-2xl font-semibold mt-1 text-gray-900 dark:text-white">
                {metrics.requestRate['1h'].toFixed(2)} <span className="text-xs md:text-sm text-gray-500">req/s</span>
              </p>
            </div>
            <TrendingUp className="h-6 w-6 md:h-8 md:w-8 text-blue-500 flex-shrink-0 ml-2" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">Error-Rate (1h)</p>
              <p className="text-xl md:text-2xl font-semibold mt-1 text-gray-900 dark:text-white">
                {metrics.errorRate['1h'].total.toFixed(2)} <span className="text-xs md:text-sm text-gray-500">err/s</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                4xx: {metrics.errorRate['1h'].rate4xx.toFixed(2)} | 5xx: {metrics.errorRate['1h'].rate5xx.toFixed(2)}
              </p>
            </div>
            <AlertTriangle className="h-6 w-6 md:h-8 md:w-8 text-red-500 flex-shrink-0 ml-2" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 sm:col-span-2 md:col-span-1">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">p95 Latency (1h)</p>
              <p className="text-xl md:text-2xl font-semibold mt-1 text-gray-900 dark:text-white">
                {metrics.latency['1h'].p95} <span className="text-xs md:text-sm text-gray-500">ms</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                p99: {metrics.latency['1h'].p99}ms | p50: {metrics.latency['1h'].p50}ms
              </p>
            </div>
            <Clock className="h-6 w-6 md:h-8 md:w-8 text-green-500 flex-shrink-0 ml-2" />
          </div>
        </div>
      </div>

      {/* Steuerung - Mobile optimiert */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 md:p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-4 w-full sm:w-auto">
            <label className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Zeitraum:</label>
            <select
              value={timeWindow}
              onChange={(e) => setTimeWindow(e.target.value as '1h' | '6h' | '24h')}
              className="flex-1 sm:flex-none border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm focus:ring-primary focus:border-primary bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="1h">Letzte Stunde</option>
              <option value="6h">Letzte 6 Stunden</option>
              <option value="24h">Letzte 24 Stunden</option>
            </select>
          </div>
          <Button
            onClick={() => {
              refetchMetrics();
            }}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Charts - Mobile optimierte Höhen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Request-Rate Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-blue-500" />
            <span className="truncate">Request-Rate</span>
          </h3>
          {isLoadingTimeSeries ? (
            <div className="h-48 md:h-64 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : hasEnoughData ? (
            <ResponsiveContainer width="100%" height={timeWindow === '24h' ? 280 : 220}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="time" 
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  width={40}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line 
                  type="monotone" 
                  dataKey="requestRate" 
                  stroke="#3b82f6" 
                  strokeWidth={2} 
                  name="Requests/s"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 md:h-64 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
              <BarChart3 className="h-10 w-10 md:h-12 md:w-12 text-gray-400 mb-3" />
              <p className="text-sm font-medium">Keine Daten im Zeitraum</p>
              <p className="text-xs text-gray-400 mt-1">Mindestens 2 Datenpunkte erforderlich</p>
            </div>
          )}
        </div>

        {/* Error-Rate Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
            <span className="truncate">Error-Rate</span>
          </h3>
          {isLoadingTimeSeries ? (
            <div className="h-48 md:h-64 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={timeWindow === '24h' ? 280 : 220}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="time"
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  width={40}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="errorRate4xx" stroke="#f59e0b" strokeWidth={2} name="4xx/s" dot={false} />
                <Line type="monotone" dataKey="errorRate5xx" stroke="#ef4444" strokeWidth={2} name="5xx/s" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 md:h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
              Keine Daten verfügbar
            </div>
          )}
        </div>

        {/* Latency Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <Clock className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
            <span className="truncate">Latency (ms)</span>
          </h3>
          {isLoadingTimeSeries ? (
            <div className="h-48 md:h-64 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : hasEnoughData ? (
            <ResponsiveContainer width="100%" height={timeWindow === '24h' ? 280 : 220}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="time" 
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                  width={40}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line 
                  type="monotone" 
                  dataKey="p95Latency" 
                  stroke="#10b981" 
                  strokeWidth={2} 
                  name="p95"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="p99Latency" 
                  stroke="#059669" 
                  strokeWidth={2} 
                  name="p99"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 md:h-64 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
              <Clock className="h-10 w-10 md:h-12 md:w-12 text-gray-400 mb-3" />
              <p className="text-sm font-medium">Keine Daten im Zeitraum</p>
              <p className="text-xs text-gray-400 mt-1">Mindestens 2 Datenpunkte erforderlich</p>
            </div>
          )}
        </div>

        {/* Activity Overview */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <Activity className="h-4 w-4 md:h-5 md:w-5 text-purple-500" />
            <span className="truncate">Aktivitäts-Übersicht</span>
          </h3>
          <div className="space-y-3 md:space-y-4">
            <div>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-2">Request-Rate</p>
              <div className="grid grid-cols-3 gap-2 text-xs md:text-sm">
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">1m:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{metrics.requestRate['1m'].toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">5m:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{metrics.requestRate['5m'].toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">1h:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{metrics.requestRate['1h'].toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-2">Error-Rate</p>
              <div className="grid grid-cols-3 gap-2 text-xs md:text-sm">
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">1m:</p>
                  <p className="font-semibold text-red-600">{metrics.errorRate['1m'].total.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">5m:</p>
                  <p className="font-semibold text-red-600">{metrics.errorRate['5m'].total.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">1h:</p>
                  <p className="font-semibold text-red-600">{metrics.errorRate['1h'].total.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-2">Latency (p95)</p>
              <div className="grid grid-cols-3 gap-2 text-xs md:text-sm">
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">1m:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{metrics.latency['1m'].p95}ms</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">5m:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{metrics.latency['5m'].p95}ms</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                  <p className="text-gray-600 dark:text-gray-400">1h:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{metrics.latency['1h'].p95}ms</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Endpoints - Mobile optimiert mit horizontalem Scroll */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Langsamste Endpoints */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-900 dark:text-white">Langsamste Endpoints</h3>
          {metrics.topEndpoints.slowest.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {metrics.topEndpoints.slowest.slice(0, 10).map((endpoint, index) => (
                <div key={index} className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-medium text-xs md:text-sm truncate text-gray-900 dark:text-white">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{endpoint.method}</span> {endpoint.path}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {endpoint.count} req | p95: {endpoint.p95Duration}ms
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm md:text-lg font-semibold text-red-600">{endpoint.avgDuration}ms</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-6 md:py-8 text-sm">Keine Daten verfügbar</p>
          )}
        </div>

        {/* Meistbesuchte Endpoints */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-900 dark:text-white">Meistbesuchte Endpoints</h3>
          {metrics.topEndpoints.mostTraffic && metrics.topEndpoints.mostTraffic.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {metrics.topEndpoints.mostTraffic.slice(0, 10).map((endpoint, index) => (
                <div key={index} className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-medium text-xs md:text-sm truncate text-gray-900 dark:text-white">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{endpoint.method}</span> {endpoint.path}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Ø {endpoint.avgDuration}ms pro req
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm md:text-lg font-semibold text-blue-600">{endpoint.count.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Requests</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 md:py-8">
              <Activity className="h-6 w-6 md:h-8 md:w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">Traffic-Daten werden geladen...</p>
            </div>
          )}
        </div>

        {/* Fehlerhafteste Endpoints */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-900 dark:text-white">Fehlerhafteste Endpoints</h3>
          {metrics.topEndpoints.mostErrors.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {metrics.topEndpoints.mostErrors.slice(0, 10).map((endpoint, index) => (
                <div key={index} className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-medium text-xs md:text-sm truncate text-gray-900 dark:text-white">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{endpoint.method}</span> {endpoint.path}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {endpoint.count} req | {endpoint.errorCount} err
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm md:text-lg font-semibold text-red-600">{endpoint.errorRate.toFixed(1)}%</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-6 md:py-8 text-sm">Keine Daten verfügbar</p>
          )}
        </div>
      </div>

      {/* === RECENT ERRORS & SLOW REQUESTS === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Recent Errors */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
              <Bug className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
              <span>Fehler-Log</span>
              {errorsData?.count ? (
                <Badge variant="destructive" className="text-[10px] px-1.5 h-5">{errorsData.count}</Badge>
              ) : null}
            </h3>
            {errorsData?.uptime && (
              <span className="text-[10px] md:text-xs text-gray-500">
                Seit {new Date(errorsData.uptime.startedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          {errorsData?.errors && errorsData.errors.length > 0 ? (
            <div className="space-y-1.5">
              {errorsData.errors.slice(0, showAllErrors ? 30 : 5).map((err, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-md text-xs">
                  <Badge variant={err.statusCode >= 500 ? "destructive" : "outline"} className="text-[10px] px-1 py-0 h-4 flex-shrink-0 mt-0.5">
                    {err.statusCode}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[11px] truncate text-gray-800 dark:text-gray-200">
                      {err.method} {err.path}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-[10px] mt-0.5">
                      {new Date(err.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      {' · '}{err.duration}ms
                      {err.requestId && <span className="ml-1 opacity-60">· {err.requestId.slice(0, 8)}</span>}
                    </p>
                  </div>
                </div>
              ))}
              {errorsData.errors.length > 5 && (
                <Button variant="ghost" size="sm" className="w-full text-xs h-7 text-gray-500" onClick={() => setShowAllErrors(!showAllErrors)}>
                  {showAllErrors ? <><ChevronUp className="h-3 w-3 mr-1" /> Weniger</> : <><ChevronDown className="h-3 w-3 mr-1" /> {errorsData.errors.length - 5} weitere</>}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              <Bug className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-xs">Keine Fehler aufgezeichnet ✓</p>
            </div>
          )}
        </div>

        {/* Slow Requests */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
              <Zap className="h-4 w-4 md:h-5 md:w-5 text-amber-500" />
              <span>Langsame Requests</span>
              {slowData?.count ? (
                <Badge variant="secondary" className="text-[10px] px-1.5 h-5">&gt;{slowData.threshold}ms: {slowData.count}</Badge>
              ) : null}
            </h3>
          </div>
          {slowData?.slowRequests && slowData.slowRequests.length > 0 ? (
            <div className="space-y-1.5">
              {slowData.slowRequests.slice(0, showAllSlow ? 15 : 5).map((req, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md text-xs">
                  <Badge variant="outline" className={`text-[10px] px-1 py-0 h-4 flex-shrink-0 mt-0.5 tabular-nums ${req.duration > 1000 ? 'border-red-300 text-red-600' : 'border-amber-300 text-amber-600'}`}>
                    {req.duration}ms
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[11px] truncate text-gray-800 dark:text-gray-200">
                      {req.method} {req.path}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-[10px] mt-0.5">
                      {new Date(req.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      {' · '}Status {req.statusCode}
                    </p>
                  </div>
                </div>
              ))}
              {slowData.slowRequests.length > 5 && (
                <Button variant="ghost" size="sm" className="w-full text-xs h-7 text-gray-500" onClick={() => setShowAllSlow(!showAllSlow)}>
                  {showAllSlow ? <><ChevronUp className="h-3 w-3 mr-1" /> Weniger</> : <><ChevronDown className="h-3 w-3 mr-1" /> {slowData.slowRequests.length - 5} weitere</>}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              <Zap className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-xs">Alle Requests unter {slowData?.threshold || 500}ms ✓</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
